/**
 * Mailtrap API Service
 * Service for interacting with Mailtrap API to retrieve template information
 *
 * Mailtrap API Documentation:
 * https://api-docs.mailtrap.io/docs/mailtrap-api-docs/
 */

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - MailtrapService');

const MAILTRAP_API_BASE = 'https://send.api.mailtrap.io/api';
const MAILTRAP_TEMPLATES_API_BASE = 'https://mailtrap.io/api';

export interface MailtrapTemplate {
  uuid: string;
  name: string;
  type: string;
  created_at: string;
  updated_at: string;
}

export interface MailtrapTemplateListItem {
  uuid: string;
  name: string;
  subject: string;
}

export interface MailtrapTemplateDetails extends MailtrapTemplate {
  subject: string;
  html_body: string;
  text_body: string;
}

export interface MailtrapCredentials {
  apiKey?: string;
  accountId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMailtrapTemplate(value: unknown): value is MailtrapTemplate {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.uuid === 'string' &&
    typeof value.name === 'string' &&
    typeof value.type === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.updated_at === 'string'
  );
}

function isMailtrapTemplateDetails(
  value: unknown,
): value is MailtrapTemplateDetails {
  if (!isMailtrapTemplate(value) || !isRecord(value)) {
    return false;
  }

  return (
    typeof value.subject === 'string' &&
    typeof value.html_body === 'string' &&
    typeof value.text_body === 'string'
  );
}

function isMailtrapTemplateListItem(
  value: unknown,
): value is MailtrapTemplateListItem {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.uuid === 'string' &&
    typeof value.name === 'string' &&
    typeof value.subject === 'string'
  );
}

function stripWrappingQuotes(value: string): string {
  const trimmedValue = value.trim();
  if (
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith("'") && trimmedValue.endsWith("'"))
  ) {
    return trimmedValue.slice(1, -1).trim();
  }

  return trimmedValue;
}

function getNonEmptyValue(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  if (value.trim().length === 0) {
    return undefined;
  }

  return stripWrappingQuotes(value);
}

function getValueFromProcessEnv(keys: string[]): string | undefined {
  if (typeof process === 'undefined' || !process.env) {
    return undefined;
  }

  for (const key of keys) {
    const resolved = getNonEmptyValue(process.env[key]);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
}

function getBrowserMailtrapApiKey(): string | undefined {
  const importMetaWithEnv = import.meta as {
    env?: {
      VITE_MAILTRAP_API_KEY?: string;
      VITA_MAILTRAP_API_KEY?: string;
    };
  };

  return (
    getNonEmptyValue(importMetaWithEnv.env?.VITE_MAILTRAP_API_KEY) ||
    getNonEmptyValue(importMetaWithEnv.env?.VITA_MAILTRAP_API_KEY) ||
    getValueFromProcessEnv(['VITE_MAILTRAP_API_KEY', 'VITA_MAILTRAP_API_KEY'])
  );
}

function getBrowserMailtrapAccountId(): string | undefined {
  const importMetaWithEnv = import.meta as {
    env?: {
      VITE_MAILTRAP_ACCOUNT_ID?: string;
      VITA_MAILTRAP_ACCOUNT_ID?: string;
    };
  };

  return (
    getNonEmptyValue(importMetaWithEnv.env?.VITE_MAILTRAP_ACCOUNT_ID) ||
    getNonEmptyValue(importMetaWithEnv.env?.VITA_MAILTRAP_ACCOUNT_ID) ||
    getValueFromProcessEnv([
      'VITE_MAILTRAP_ACCOUNT_ID',
      'VITA_MAILTRAP_ACCOUNT_ID',
    ])
  );
}

export class MailtrapService {
  private static getApiKey(credentials?: MailtrapCredentials): string {
    const apiKey =
      getNonEmptyValue(credentials?.apiKey) || getBrowserMailtrapApiKey();

    if (!apiKey) {
      throw new Error('VITE_MAILTRAP_API_KEY environment variable not set');
    }

    return apiKey;
  }

  private static getAccountId(credentials?: MailtrapCredentials): string {
    const accountId =
      getNonEmptyValue(credentials?.accountId) ||
      getBrowserMailtrapAccountId();

    if (!accountId) {
      throw new Error('VITE_MAILTRAP_ACCOUNT_ID environment variable not set');
    }

    return accountId;
  }

  private static async fetchMailtrap(
    endpoint: string,
    method: string = 'GET',
    body?: unknown,
    credentials?: MailtrapCredentials,
  ): Promise<Response> {
    const apiKey = this.getApiKey(credentials);

    const response = await fetch(`${MAILTRAP_API_BASE}${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Mailtrap API error', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(`Mailtrap API error: ${response.statusText}`);
    }

    return response;
  }

  private static async fetchMailtrapTemplatesApi(
    endpoint: string,
    method: string = 'GET',
    body?: unknown,
    credentials?: MailtrapCredentials,
  ): Promise<Response> {
    const apiKey = this.getApiKey(credentials);

    const response = await fetch(`${MAILTRAP_TEMPLATES_API_BASE}${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Mailtrap Templates API error', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(`Mailtrap API error: ${response.statusText}`);
    }

    return response;
  }

  /**
   * List all templates from Mailtrap
   * Note: This requires the Mailtrap API endpoint and may not be available in all plans
   */
  static async listTemplates(
    credentials?: MailtrapCredentials,
  ): Promise<MailtrapTemplate[]> {
    try {
      const accountId = this.getAccountId(credentials);
      const response = await this.fetchMailtrap(
        `/accounts/${accountId}/templates`,
        'GET',
        undefined,
        credentials,
      );
      const json: unknown = await response.json();
      const templates = Array.isArray(json)
        ? json.filter(isMailtrapTemplate)
        : [];

      logger.info(`Fetched ${templates.length} templates from Mailtrap`);
      return templates;
    } catch (error) {
      logger.error('Error fetching templates from Mailtrap', {
        error: error instanceof Error ? error.message : String(error),
      });
      // Return empty array instead of throwing to allow graceful degradation
      return [];
    }
  }

  /**
   * List all templates from Mailtrap with full details (uuid, name, subject)
   * Uses the management API endpoint to retrieve template list with subjects
   */
  static async listTemplatesFull(
    credentials?: MailtrapCredentials,
  ): Promise<MailtrapTemplateListItem[]> {
    try {
      const accountId = this.getAccountId(credentials);
      const response = await this.fetchMailtrapTemplatesApi(
        `/accounts/${accountId}/email_templates`,
        'GET',
        undefined,
        credentials,
      );
      const json: unknown = await response.json();
      const templates = Array.isArray(json)
        ? json.filter(isMailtrapTemplateListItem)
        : [];

      logger.info(`Fetched ${templates.length} full templates from Mailtrap`);
      return templates;
    } catch (error) {
      logger.error('Error fetching full templates from Mailtrap', {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /**
   * Get template details by UUID from Mailtrap
   */
  static async getTemplate(
    uuid: string,
    credentials?: MailtrapCredentials,
  ): Promise<MailtrapTemplateDetails | null> {
    try {
      const accountId = this.getAccountId(credentials);
      const response = await this.fetchMailtrap(
        `/accounts/${accountId}/templates/${uuid}`,
        'GET',
        undefined,
        credentials,
      );
      const json: unknown = await response.json();
      if (!isMailtrapTemplateDetails(json)) {
        logger.warn('Invalid template payload from Mailtrap', { uuid });
        return null;
      }

      logger.info('Fetched template from Mailtrap', { uuid });
      return json;
    } catch (error) {
      logger.error('Error fetching template from Mailtrap', {
        error: error instanceof Error ? error.message : String(error),
        uuid,
      });
      return null;
    }
  }

  /**
   * Check if Mailtrap integration is available
   */
  static isAvailable(): boolean {
    try {
      const apiKey = getBrowserMailtrapApiKey();

      return !!apiKey;
    } catch {
      return false;
    }
  }

  /**
   * Sync template from Mailtrap
   * Fetches template details and returns data suitable for creating/updating local template
   */
  static async syncTemplate(
    uuid: string,
    credentials?: MailtrapCredentials,
  ): Promise<{
    template_id: string;
    name: string;
    subject: string;
  } | null> {
    try {
      const template = await this.getTemplate(uuid, credentials);

      if (!template) {
        return null;
      }

      return {
        template_id: template.uuid,
        name: template.name,
        subject: template.subject,
      };
    } catch (error) {
      logger.error('Error syncing template from Mailtrap', {
        error: error instanceof Error ? error.message : String(error),
        uuid,
      });
      return null;
    }
  }
}
